"use client";

export default class NodeMaterialSystem {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Computes the visual material properties for a single node.
   * @param {Object} vibe - The vibe node data.
   * @param {Object|null} territory - The territory the node belongs to.
   * @param {number} time - Current application time.
   * @param {Object} state - Global application state.
   * @returns {Object} The computed material properties.
   */
  getMaterial(vibe, territory, time, state) {
    const chaos = vibe.axes.chaos / 100;
    const organicness = vibe.axes.organicness / 100;
    const futurity = vibe.axes.futurity / 100;
    const isSelected = state.selectedVibeIds.includes(vibe.id);
    const isHovered = state.hoveredVibeId === vibe.id;

    // 1. Base Color & Palette Modulation
    let baseColor = this.hexToRgb(vibe.colorPalette[0] || '#ffffff');

    // Apply subtle chromatic drift based on chaos
    const drift = Math.sin(time * 0.5 + vibe.driftOffset) * (chaos * 0.1);
    baseColor.r = Math.min(255, Math.max(0, baseColor.r + drift * 255));

    // 2. Territory Influence (Blending)
    let territoryTint = { r: 0, g: 0, b: 0, a: 0 };
    if (territory) {
      const tColor = this.hexToRgb(territory.color);
      territoryTint = { ...tColor, a: 0.3 };
    }

    // 3. Pulse Rhythm (Breathing)
    const pulseFrequency = 0.5 + (chaos * 2.0) + (isHovered ? 2.0 : 0);
    const pulseAmplitude = 0.05 + (chaos * 0.15);
    const pulse = 1.0 + Math.sin(time * pulseFrequency + vibe.waveOffset) * pulseAmplitude;

    // 4. Edge Softness & Glow
    const edgeSoftness = 0.5 + (organicness * 0.5) - (futurity * 0.3);
    const glowIntensity = 0.1 + (isHovered ? 0.3 : 0) + (isSelected ? 0.4 : 0);

    // 5. Final Property Assembly
    return {
      color: `rgba(${baseColor.r}, ${baseColor.g}, ${baseColor.b}, ${0.8 + (isHovered ? 0.2 : 0)})`,
      territoryColor: territory
        ? `rgba(${territoryTint.r}, ${territoryTint.g}, ${territoryTint.b}, ${territoryTint.a})`
        : 'rgba(0,0,0,0)',
      pulse,
      glow: glowIntensity,
      edgeSoftness,
      radiusMultiplier: pulse,
      opacity: 0.7 + (isHovered ? 0.3 : 0)
    };
  }

  hexToRgb(hex) {
    const normalized = hex.replace('#', '');
    const value = normalized.length === 3
      ? normalized.split('').map(char => char + char).join('')
      : normalized;
    const int = Number.parseInt(value, 16);
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    };
  }
}